import {
  CalendarCheck2,
  CircleDollarSign,
  Clock3,
  ShieldAlert,
  TrendingUp,
  UserRoundPlus,
  WalletCards,
} from "lucide-react";
import { useGetList } from "ra-core";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, Deal, Task } from "../types";

const money = new Intl.NumberFormat("en-EG", {
  style: "currency",
  currency: "EGP",
  maximumFractionDigits: 0,
});

const stageActions: Record<string, string> = {
  "new-lead": "Make first contact",
  contacted: "Complete qualification",
  qualified: "Book consultation",
  "consultation-booked": "Confirm attendance",
  attended: "Prepare proposal",
  "proposal-sent": "Discuss patient concern",
  accepted: "Collect deposit",
};

const stageTone: Record<string, string> = {
  "new-lead": "bg-slate-100 text-slate-700",
  contacted: "bg-blue-50 text-blue-700",
  qualified: "bg-cyan-50 text-cyan-700",
  "consultation-booked": "bg-indigo-50 text-indigo-700",
  attended: "bg-amber-50 text-amber-700",
  "proposal-sent": "bg-violet-50 text-violet-700",
  accepted: "bg-emerald-50 text-emerald-700",
};

export const Dashboard = () => {
  const { dealStages } = useConfigurationContext();
  const { data: deals = [], isPending: dealsPending } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "updated_at", order: "DESC" },
    },
  );
  const { data: contacts = [], isPending: contactsPending } =
    useGetList<Contact>("contacts", {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "last_seen", order: "DESC" },
    });
  const { data: tasks = [], isPending: tasksPending } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 12 },
      sort: { field: "due_date", order: "ASC" },
      filter: { "done_date@is": null },
    },
  );

  if (dealsPending || contactsPending || tasksPending) return null;

  const contactById = new Map(
    contacts.map((contact) => [String(contact.id), contact]),
  );
  const deposited = deals.filter((deal) => deal.stage === "deposit-paid");
  const atRisk = deals.filter((deal) =>
    ["attended", "proposal-sent", "accepted"].includes(deal.stage),
  );
  const lost = deals.filter((deal) => deal.stage === "lost");
  const securedValue = deposited.reduce((sum, deal) => sum + deal.amount, 0);
  const riskValue = atRisk.reduce((sum, deal) => sum + deal.amount, 0);
  const lostValue = lost.reduce((sum, deal) => sum + deal.amount, 0);
  const followUps = deals
    .filter((deal) => stageActions[deal.stage])
    .sort(
      (left, right) =>
        new Date(left.updated_at).getTime() -
        new Date(right.updated_at).getTime(),
    )
    .slice(0, 6);

  return (
    <div className="space-y-6 pb-10 text-foreground">
      <section className="flex flex-col gap-4 border-b pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">
            <span className="h-2 w-2 rounded-full bg-teal-600" />
            Fictional demo data
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground">
            Revenue Command Center
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Move every enquiry toward a consultation, proposal, and recorded
            deposit.
          </p>
        </div>
        <Link
          to="/deals/create"
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-teal-700 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-800"
        >
          <UserRoundPlus className="h-4 w-4" /> New lead case
        </Link>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Metric
          icon={TrendingUp}
          label="Secured Case Value"
          value={money.format(securedValue)}
          helper={`${deposited.length} cases reached deposit paid`}
        />
        <Metric
          icon={ShieldAlert}
          label="Revenue at Risk"
          value={money.format(riskValue)}
          helper={`${atRisk.length} attended or proposed cases`}
          warning
        />
        <Metric
          icon={WalletCards}
          label="Deposits Recorded"
          value={String(deposited.length)}
          helper="No payment amount is inferred"
        />
        <Metric
          icon={CircleDollarSign}
          label="Lost Opportunities"
          value={money.format(lostValue)}
          helper={`${lost.length} cases marked lost`}
        />
      </section>

      <section className="overflow-hidden rounded-xl border bg-card">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="font-semibold text-foreground">Pipeline Overview</h3>
            <p className="text-sm text-muted-foreground">
              Live counts from the case pipeline
            </p>
          </div>
          <Link
            to="/deals"
            className="text-sm font-semibold text-teal-700 hover:text-teal-800"
          >
            Open pipeline
          </Link>
        </div>
        <div className="flex min-w-max divide-x overflow-x-auto">
          {dealStages.map((stage) => (
            <div key={stage.value} className="min-w-36 flex-1 px-4 py-4">
              <p className="text-xs font-medium text-muted-foreground">
                {stage.label}
              </p>
              <p className="mt-1 text-2xl font-semibold text-foreground">
                {deals.filter((deal) => deal.stage === stage.value).length}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section className="overflow-hidden rounded-xl border bg-card">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <div>
              <h3 className="font-semibold text-foreground">
                Needs Follow-up Now
              </h3>
              <p className="text-sm text-muted-foreground">
                Oldest active opportunities first
              </p>
            </div>
            <Clock3 className="h-5 w-5 text-teal-700" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-5 py-3 font-semibold">Patient lead</th>
                  <th className="px-4 py-3 font-semibold">Service</th>
                  <th className="px-4 py-3 font-semibold">Stage</th>
                  <th className="px-4 py-3 font-semibold">Next action</th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Case value
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {followUps.map((deal) => {
                  const contact = contactById.get(
                    String(deal.contact_ids?.[0]),
                  );
                  const patient = contact
                    ? `${contact.first_name} ${contact.last_name}`
                    : "Unassigned lead";
                  const stage = dealStages.find(
                    (item) => item.value === deal.stage,
                  );
                  return (
                    <tr key={deal.id} className="transition hover:bg-muted/30">
                      <td className="px-5 py-4">
                        <Link
                          to={`/deals/${deal.id}/show`}
                          className="font-semibold text-foreground hover:text-teal-600"
                        >
                          {patient}
                        </Link>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {deal.name}
                        </p>
                      </td>
                      <td className="px-4 py-4 capitalize text-muted-foreground">
                        {deal.category.replace("-", " ")}
                      </td>
                      <td className="px-4 py-4">
                        <Badge
                          className={
                            stageTone[deal.stage] ??
                            "bg-slate-100 text-slate-700"
                          }
                          variant="secondary"
                        >
                          {stage?.label ?? deal.stage}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-muted-foreground">
                        {stageActions[deal.stage]}
                      </td>
                      <td className="px-4 py-4 text-right font-semibold text-foreground">
                        {money.format(deal.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="space-y-4">
          <Card className="gap-0 py-0">
            <CardHeader className="border-b py-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarCheck2 className="h-5 w-5 text-teal-700" /> Today's
                Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="divide-y px-5">
              {tasks.slice(0, 6).map((task) => {
                const contact = contactById.get(String(task.contact_id));
                return (
                  <div key={task.id} className="py-4">
                    <p className="text-sm font-semibold text-foreground">
                      {task.text}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {contact
                        ? `${contact.first_name} ${contact.last_name}`
                        : "General task"}
                    </p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
          <div className="rounded-xl border border-teal-200 bg-teal-50 p-5 dark:border-teal-900 dark:bg-teal-950/40">
            <h3 className="font-semibold text-teal-950 dark:text-teal-100">
              Clinical safety boundary
            </h3>
            <p className="mt-2 text-sm leading-6 text-teal-900/80 dark:text-teal-100/80">
              This workflow organizes enquiries and commercial follow-up. It
              does not diagnose, rank treatment, or decide clinical suitability.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
};

const Metric = ({
  icon: Icon,
  label,
  value,
  helper,
  warning = false,
}: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  helper: string;
  warning?: boolean;
}) => (
  <Card className="gap-3 py-5 shadow-none">
    <CardContent className="px-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p
            className={`mt-2 text-2xl font-bold tracking-tight ${
              warning ? "text-amber-600" : "text-foreground"
            }`}
          >
            {value}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">{helper}</p>
        </div>
        <span
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${
            warning ? "bg-amber-50 text-amber-700" : "bg-teal-50 text-teal-700"
          }`}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </CardContent>
  </Card>
);
